import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, forwardRef, Input, ViewChild, OnInit } from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormBuilder,
  UntypedFormControl, UntypedFormGroup, ValidationErrors, Validator, Validators
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent
} from '@angular/material/autocomplete';
import { distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';

import { FormGroupConfig } from 'core/form-config-type';
import { Countries, getRegions, isInternationalCountry } from 'core/geography';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import * as CustomValidators from 'core/validators';
import { getSafe } from 'helpers';

import { AddressType } from 'generated/graphql';
import { AddressAutocompleter } from './address-autocompleter';
import { zip } from 'lodash-es';

export interface AddressFormModel {
  // Model of form value
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  verified?: boolean;
  addressType: AddressType;
}

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AddressFormComponent),
      multi: true
    }
  ]
})
export class AddressFormComponent
  extends WithDisposableSubject()
  implements ControlValueAccessor, Validator, OnInit {
  formGroup: UntypedFormGroup;

  @ViewChild('auto', { static: false }) auto: MatAutocomplete;

  /** Helper function for the autocompleter */
  addressAutocompleter = new AddressAutocompleter(this._http);

  /** Autocomplete control, not part of the actual form
   *  used to set hidden value for streetAddress.
   */
  autocompleteAddressControl: UntypedFormControl;

  /**
   * Created in the constructor to allow for closure
   * access to the form group
   */
  displayAutocompleteAddrFn: (value: any) => string;

  countries = Countries;

  // This is set based upon the selected country
  states = [];

  /** Some address inputs might not have a confirmed checkbox */
  @Input() hideConfirmedCheckbox: boolean;

  private _onTouched = () => { };

  constructor(
    private _http: HttpClient,
    private _cd: ChangeDetectorRef,
    private _fb: UntypedFormBuilder) {
    super();

    const config: FormGroupConfig<AddressFormModel> = {
      country: [null, Validators.required],
      streetAddress: [''],
      city: [null],
      state: [null, []],
      zip: [null, [Validators.maxLength(10)]],
      verified: [null],
      addressType: [AddressType.Physical, [Validators.required]]
    };


    this.formGroup = this._fb.group(config);

    // Hack to bind the display function for the address
    // autocomplete to the hidden value

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _self = this;
    this.displayAutocompleteAddrFn = (value: any) => {
      if (value?.streetAddress) {
        return value.streetAddress;
      }
      return getSafe(() => _self.formGroup.get('streetAddress').value || '', '');
    };

    this.autocompleteAddressControl = new UntypedFormControl();


    this.initAddressAutocomplete();
  }

  markAllAsTouched() {
    Object.values(this.formGroup.controls).forEach(control => {
      control.markAsTouched();
      control.updateValueAndValidity();
    });
    this._cd.markForCheck();
  }


  ngOnInit() {
    this.watchCountry();
  }


  /** Control Value Accessor Implementmenation */
  writeValue(obj: AddressFormModel): void {
    if (obj) {
      if (obj.country) {
        this.states = getRegions(obj.country);
      }
      this.autocompleteAddressControl.setValue({ streetAddress: obj?.streetAddress });
      this.formGroup.patchValue(obj, { emitEvent: false });
    }
    // this._cd.markForCheck();
    // this._cd.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.formGroup.valueChanges.pipe(
      takeUntil(this.disposer)
    ).subscribe(
      value => {
        this.formGroup.controls.state.markAsTouched();
        if (this.hideConfirmedCheckbox) {
          value.verified = undefined;
        }
        fn(value);
      }

    );
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  /** Validator Implementation */
  validate(control: AbstractControl): ValidationErrors {
    if (this.formGroup.valid) {
      return null;
    }
    // if (!this.formGroup.valid) {
    //   this.formGroup.markAllAsTouched();
    // }
    return { address: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.formGroup.valueChanges.pipe(
      takeUntil(this.disposer)
    ).subscribe(fn);
  }

  getValue(): AddressFormModel {
    return this.formGroup.value;
  }

  onAddressSelected(event: MatAutocompleteSelectedEvent) {
    this.formGroup.patchValue(event.option.value);
  }

  onAddressBlurSetType() {
    const addressValue = this.formGroup.get('streetAddress').value;

    // Check to see if the address value looks like a po box
    if (addressValue && addressValue.match(/(P\.?O\.? Box|PO Box)/i)) {
      this.formGroup.get('addressType').setValue(AddressType.Mailing);
    }
  }

  isInternationalCountry(country: string): boolean {
    return isInternationalCountry(country);
  }

  // Internals

  private watchCountry() {
    // Watch for changes to country
    this.formGroup.get('country').valueChanges
      .pipe(
        startWith(this.formGroup.get('country').value),
        distinctUntilChanged((x, y) => x === y)
      )
      .subscribe((country) => {
        const stateControl = this.formGroup.get('state');
        const zipControl = this.formGroup.get('zip');
        this.states = getRegions(country);

        // US/CA require state
        if (!isInternationalCountry(country)) {
          stateControl.setValidators([Validators.required]);
          zipControl.removeValidators([Validators.required]);
          const invalidState = !this.states.map(t => t.abbreviation).includes(stateControl.value);
          if (invalidState) {
            stateControl.setValue(null);
          }
        } else {
          // International (UK/European) countries require zip but not state
          zipControl.setValidators([Validators.required]);
          stateControl.removeValidators([Validators.required]);
          stateControl.setValue(null);
        }

        zipControl.updateValueAndValidity({ emitEvent: false });
        stateControl.updateValueAndValidity({ emitEvent: false });
      });
  }

  private initAddressAutocomplete() {
    this.autocompleteAddressControl.valueChanges.subscribe((searchText) => {
      if (typeof searchText === 'string' || searchText == null) {
        this.formGroup.get('streetAddress').setValue(searchText);
      }
      this.addressAutocompleter.search(
        searchText,
        this.formGroup.value.country
      );
    });
  }
}
